import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Archive,
  BarChart3,
  BookMarked,
  CalendarDays,
  BookOpen,
  ChevronDown,
  Compass,
  Dna,
  Fan,
  FileCheck2,
  MapPin,
  ShieldAlert,
  GitFork,
  HelpCircle,
  LogOut,
  MessageSquarePlus,
  Menu,
  Plus,
  Route,
  Search,
  Settings,
  Telescope,
  TreePine,
  TrendingUp,
  Trophy,
  Trash2,
  Upload,
  User,
  Users,
  X,
  type LucideIcon,
  MessagesSquare,
} from 'lucide-react';
import { Rundgang } from '@/components/Rundgang/Rundgang';
import { AnliegenPopup } from '@/components/Anliegen/AnliegenPopup';
import { rundgangNochmal } from '@/api/rundgang.api';
import { useTreeStore } from '@/store/useTreeStore';
import { useTreeContextStore } from '@/store/useTreeContextStore';
import { useAuthStore } from '@/store/useAuthStore';
import { COMMUNITY_URL, ladeGemeinschaftszugang } from '@/api/gemeinschaft.api';
import { useTreeFeatures } from '@/hooks/useTreeFeatures';
import { useTexte } from '@/hooks/useTexte';
import { logout } from '@/api/auth.api';
import { deleteEntireTree } from '@/api/tree.api';
import { listTrees } from '@/api/trees.api';
import { PersonPanel } from '@/components/PersonPanel/PersonPanel';
import { PersonForm } from '@/components/PersonForm/PersonForm';
import { ImportModal } from '@/components/Import/ImportModal';
import { CommandPalette } from '@/components/common/CommandPalette';
import { TreeSwitcher } from '@/components/Layout/TreeSwitcher';
import { EXPORT_ITEMS, downloadExportFile } from '@/components/TopBar/ExportMenu';
import { ladeChronik } from '@/api/chronik.api';
import {
  CHRONIK_SORTIERUNGEN,
  CHRONIK_SORTIERUNG_BESCHREIBUNG,
  CHRONIK_SORTIERUNG_LABELS,
  type ChronikSortierung,
} from '@genealogy-app/shared';

// ─── Navigation groups ──────────────────────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  icon?: LucideIcon;
  permission: string;
  /** Sensibles Recht (Medizin) – Nicht-Admins brauchen es explizit zugewiesen. */
  sensitive?: boolean;
  /**
   * Funktion aus dem Tarif, die dieser Eintrag braucht (Schlüssel aus
   * FEATURES). Fehlt sie im Tarif des Stammbaums, erscheint der Eintrag nicht.
   *
   * Ohne Angabe hängt der Eintrag an keinem Tarif – Stammbaum, Suche und
   * Belege gibt es immer, sonst wäre die Anwendung nicht benutzbar.
   */
  feature?: string;
  /**
   * Das Ziel liegt AUSSERHALB dieser Anwendung – dann ist `path` eine volle
   * Adresse und der Router nicht zuständig. Bisher trifft das nur auf die
   * Gemeinschaft zu, die in einem eigenen Container unter community.<domain>
   * läuft.
   */
  extern?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Zu einem Menüpunkt springen.
 *
 * Externe Ziele – bisher nur die Gemeinschaft – liegen auf einer anderen
 * Domain. Der Router kennt sie nicht; `navigate()` würde sie als Pfad dieser
 * Anwendung deuten und auf einer Seite landen, die es hier nicht gibt.
 */
function springeZu(navigate: (pfad: string) => void, item: NavItem): void {
  if (item.extern) window.location.assign(item.path);
  else navigate(item.path);
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Erkunden',
    items: [
      { label: 'Stammbaum', path: '/', icon: TreePine, permission: 'persons.view' },
      { label: 'Fächerdiagramm', path: '/fan-chart', icon: Fan, permission: 'persons.view', feature: 'fanChart' },
      { label: 'Sternenhimmel', path: '/cosmos', icon: Telescope, permission: 'cosmos.view', feature: 'cosmos' },
    ],
  },
  {
    label: 'Analyse',
    items: [
      { label: 'Statistiken', path: '/statistics', icon: BarChart3, permission: 'statistics.view', feature: 'statistics' },
      { label: 'Lebenslinien', path: '/lifelines', icon: TrendingUp, permission: 'lifelines.view', feature: 'lifelines' },
      { label: 'Kalender', path: '/calendar', icon: CalendarDays, permission: 'calendar.view', feature: 'calendar' },
    ],
  },
  {
    label: 'Archiv',
    items: [
      { label: 'Archiv', path: '/archive', icon: Archive, permission: 'archive.view', feature: 'archive' },
    ],
  },
  {
    label: 'Forschung',
    items: [
      { label: 'Pfadfinder', path: '/relationship-finder', icon: Route, permission: 'relationshipFinder.view', feature: 'relationshipFinder' },
      { label: 'Belege', path: '/sources', icon: FileCheck2, permission: 'persons.view', feature: 'sourceCoverage' },
      { label: 'Plausibilität', path: '/plausibility', icon: ShieldAlert, permission: 'persons.view', feature: 'plausibility' },
      // Orte hängen an keinem Tarif: Sie sind kein Extra, sondern die
      // Voraussetzung dafür, dass die Karte stimmt – und die Karte gibt es
      // überall, wo es Personen gibt.
      { label: 'Orte', path: '/orte', icon: MapPin, permission: 'persons.view' },
      { label: 'Offene Fragen', path: '/cold-cases', icon: HelpCircle, permission: 'coldCases.view', feature: 'coldCases' },
      { label: 'Vererbung', path: '/inheritance', icon: GitFork, permission: 'medical.view', sensitive: true, feature: 'medical' },
      { label: 'Diagnose-Katalog', path: '/medical-catalog', icon: BookMarked, permission: 'medical.view', sensitive: true, feature: 'medical' },
      { label: 'Medizin-Statistik', path: '/medical-stats', icon: Dna, permission: 'medical.view', sensitive: true, feature: 'medical' },
    ],
  },
  {
    label: 'Spiele',
    items: [
      { label: 'Entdecker-Karte', path: '/fog-of-war', icon: Compass, permission: 'fogOfWar.view', feature: 'fogOfWar' },
      { label: 'Familien-Quiz', path: '/quiz', icon: HelpCircle, permission: 'quiz.view', feature: 'quiz' },
      { label: 'Trophäenraum', path: '/achievements', icon: Trophy, permission: 'achievements.view', feature: 'achievements' },
    ],
  },
];

// Zwei Prüfungen, und beide müssen zustimmen: das RECHT sagt, was jemand in
// diesem Stammbaum darf, die FUNKTION, ob es die Sache hier überhaupt gibt.
// Vorher hing das Menü nur am Recht – ein Kunde im kleinen Tarif sah alles,
// und der Server wies erst beim Klick mit 402 ab.
function useFilteredNavGroups(): NavGroup[] {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasSensitivePermission = useAuthStore((s) => s.hasSensitivePermission);
  const { hat } = useTreeFeatures();

  // Die Gemeinschaft steht neben, nicht in den Stammbäumen – seit sie ein
  // eigener Container ist, sogar auf einer eigenen Domain. Ob der Weg dorthin
  // erscheint, hängt am Tarif des KONTOS und am Schalter des Betreibers;
  // beides weiß nur der Server, und beides beantwortet er in einem Zug.
  // Deshalb hier eine eigene Abfrage statt der Baum-Funktionsliste.
  //
  // Anders als bei den Baum-Funktionen gilt hier: solange nichts geladen ist,
  // erscheint der Punkt NICHT. Die Gemeinschaft ist abschaltbar; ein Menüpunkt,
  // der beim Laden kurz aufblitzt und wieder verschwindet, sähe nach einer
  // Fehlfunktion aus.
  const { data: gemeinschaft } = useQuery({
    queryKey: ['gemeinschaft', 'zugang'],
    queryFn: ladeGemeinschaftszugang,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const gefiltert = NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const darf = item.sensitive
          ? hasSensitivePermission(item.permission)
          : hasPermission(item.permission);
        return darf && (!item.feature || hat(item.feature));
      }),
    }))
    .filter((group) => group.items.length > 0);

  // Ohne bekannte Adresse kein Link: Wer VITE_COMMUNITY_URL beim Bauen
  // vergisst, bekommt keinen Menüpunkt ins Leere.
  if (!COMMUNITY_URL || !gemeinschaft?.lesen) return gefiltert;

  return [
    ...gefiltert,
    {
      label: 'Gemeinschaft',
      // Kein Recht: Rechte gelten je Stammbaum, die Gemeinschaft gehört zum
      // Konto. Die Frage „darf ich hier rein" hat `gemeinschaft.lesen` oben
      // schon beantwortet.
      items: [
        {
          label: 'Forum',
          path: COMMUNITY_URL,
          icon: MessagesSquare,
          permission: '',
          extern: true,
        },
      ],
    },
  ];
}

// Diese Ziele erscheinen am Desktop als Direktlinks statt in einem Dropdown.
// „Stammbaum" (/) bleibt außen vor: Das Logo links ist bereits der Home-Link.
//
// „Suche" stand hier ebenfalls – gleich neben dem Suchfeld auf der rechten
// Seite. Zwei Wege zur selben Sache in derselben Zeile; der Menüpunkt ist raus.
const DIRECT_LINK_PATHS = ['/archive'];

function splitNavForDesktop(groups: NavGroup[]): { direct: NavItem[]; dropdownGroups: NavGroup[] } {
  const direct = groups
    .flatMap((group) => group.items)
    .filter((item) => DIRECT_LINK_PATHS.includes(item.path))
    .sort((a, b) => DIRECT_LINK_PATHS.indexOf(a.path) - DIRECT_LINK_PATHS.indexOf(b.path));

  const dropdownGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !DIRECT_LINK_PATHS.includes(item.path)),
    }))
    .filter((group) => group.items.length > 0);

  return { direct, dropdownGroups };
}

// ─── NavDirectLink ───────────────────────────────────────────────────────────

function NavDirectLink({ item }: { item: NavItem }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive =
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);

  return (
    <button
      onClick={() => springeZu(navigate, item)}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-100 hover:text-slate-900 ${
        isActive ? 'text-blue-600' : 'text-slate-600'
      }`}
    >
      {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
      {item.label}
    </button>
  );
}

// ─── NavDropdown ─────────────────────────────────────────────────────────────

interface NavDropdownProps {
  label: string;
  items: NavItem[];
}

function NavDropdown({ label, items }: NavDropdownProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isGroupActive = items.some((item) =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  );

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-100 hover:text-slate-900 ${
          isGroupActive ? 'text-blue-600' : 'text-slate-600'
        }`}
      >
        {label}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.5}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-44 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl z-50">
          {items.map((item) => {
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  springeZu(navigate, item);
                  setOpen(false);
                }}
                className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg ${
                  isActive
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {item.icon && (
                  <item.icon
                    className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-400'}`}
                    aria-hidden="true"
                  />
                )}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── User-Menü ───────────────────────────────────────────────────────────────

interface UserMenuProps {
  onOpenImport: () => void;
  onOpenDelete: () => void;
}

/**
 * Besitze ich den gerade aktiven Stammbaum?
 *
 * Der Server liefert das je Baum mit; wir lesen es aus derselben Abfrage, die
 * auch der Baum-Umschalter benutzt – kein zusätzlicher Aufruf.
 */
function useIstBesitzer(): boolean {
  const activeTreeId = useTreeContextStore((s) => s.activeTreeId);
  const { data: trees } = useQuery({ queryKey: ['trees', 'accessible'], queryFn: listTrees });
  return !!trees?.find((t) => t.id === activeTreeId)?.isOwner;
}

function UserMenu({ onOpenImport, onOpenDelete }: UserMenuProps) {
  const navigate = useNavigate();
  const { user, isAdmin, clearAuth } = useAuthStore();
  const istBesitzer = useIstBesitzer();
  const { hat } = useTreeFeatures();
  // Der Tarif entscheidet auf dem Server; das Ausblenden hier erspart nur den
  // Klick auf etwas, das ohnehin abgewiesen würde.
  const darfChronik = hat('chronik');
  // Löschen darf laut Server der Besitzer (assertDarfVerwalten) – bisher stand
  // hier der Konto-Admin, und der Besitzer seines eigenen Baums kam nicht an
  // den Punkt heran.
  const darfLoeschen = istBesitzer || isAdmin;
  // Der Export gibt nur heraus, was der Nutzer auf den Seiten ohnehin sieht –
  // die Route verlangt serverseitig auch nichts weiter.
  const darfExportieren = useAuthStore((s) => s.hasPermission('persons.view'));
  // Der Import schreibt Personen – dieselbe Schranke wie beim Anlegen von Hand.
  const darfImportieren = useAuthStore((s) => s.hasPermission('persons.manage'));
  const darfDatenverwaltung = darfImportieren || darfLoeschen || darfExportieren;
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleExport = async (item: (typeof EXPORT_ITEMS)[number]) => {
    setDownloading(item.path);
    try {
      await downloadExportFile(item.path, item.filename);
      toast.success(`${item.label} abgeschlossen.`);
      setOpen(false);
    } catch {
      toast.error(`${item.label} fehlgeschlagen.`);
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Auch bei Fehler ausloggen
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Benutzermenü"
        className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
      >
        <User className="h-4 w-4 text-slate-400" aria-hidden="true" />
        <span className="hidden max-w-[120px] truncate sm:inline">{user?.email}</span>
        <ChevronDown className="h-3 w-3 text-slate-400" aria-hidden="true" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-60 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl">
          {/* Nutzerinfo */}
          <div className="border-b border-slate-100 px-4 py-2.5">
            <p className="text-xs text-slate-400">Angemeldet als</p>
            <p className="truncate text-sm font-medium text-slate-900">{user?.email}</p>
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
              {isAdmin ? 'Admin' : 'Betrachter'}
            </span>
          </div>

          {/* Die Verwaltung ist in die Betreiberkonsole gezogen – eigene
              Anwendung, eigene Anmeldung. Hier steht kein Verweis darauf:
              sie liegt auf einer anderen Adresse, die diese Anwendung nicht
              kennen muss.

              Geblieben ist, was dem Kunden gehört: wer diesen Stammbaum
              besitzt, verwaltet seine Mitglieder selbst. */}
          {istBesitzer && (
            <>
              <button
                onClick={() => { setOpen(false); navigate('/tree-settings'); }}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <TreePine className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Stammbaum
              </button>
              <button
                onClick={() => { setOpen(false); navigate('/members'); }}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Mitglieder
              </button>
            </>
          )}

          {/* Konto, Passwort, zweiter Faktor, Tarif – längst nicht mehr nur
              Sicherheit, deshalb heißt es hier auch nicht mehr so. */}
          <button
            onClick={() => { setOpen(false); navigate('/settings/security'); }}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            <Settings className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Einstellungen
          </button>

          {/* Chronik: gehört zum ganzen Stammbaum, nicht zur Ansicht – deshalb
              hier und nicht mehr in der Werkzeugleiste des Baums. */}
          {darfChronik && <ChronikEintrag onFertig={() => setOpen(false)} />}

          {/* Datenverwaltung.
              Nichts davon hängt mehr an der Konto-Rolle: Export für jeden, der
              die Daten ohnehin sieht, Import für jeden, der Personen pflegen
              darf, Löschen für den Besitzer. Genau so hält es der Server. */}
          {darfDatenverwaltung && (
            <>
              <div className="my-1 border-t border-slate-100" />
              <div className="px-4 py-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Datenverwaltung</p>
              </div>
              {darfImportieren && (
                <button
                  onClick={() => { setOpen(false); onOpenImport(); }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  Import
                </button>
              )}
              {darfExportieren &&
                EXPORT_ITEMS.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleExport(item)}
                    disabled={downloading !== null}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <span className="shrink-0 text-slate-400">
                      {downloading === item.path ? (
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                      ) : (
                        item.icon
                      )}
                    </span>
                    {item.label}
                  </button>
                ))}
              {darfLoeschen && (
                <button
                  onClick={() => { setOpen(false); onOpenDelete(); }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Stammbaum löschen
                </button>
              )}
            </>
          )}

          {/* Trennstrich nur, wenn darüber wirklich etwas steht. */}
          {darfDatenverwaltung && <div className="my-1 border-t border-slate-100" />}

          {/* Der Rundgang ist kein einmaliges Angebot: wer ihn beim ersten Mal
              weggeklickt hat, findet ihn hier wieder. */}
          <RundgangEintrag onFertig={() => setOpen(false)} />
          <VorschlagEintrag onFertig={() => setOpen(false)} />

          {/* Abmelden */}
          <div className="my-1 border-t border-slate-100" />
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Einträge, die in beiden Menüs stehen ────────────────────────────────────
//
// Chronik und Rundgang gab es zuerst nur im Desktop-Dropdown. Am Telefon
// fehlten sie damit ganz – und dort ist das Menü der einzige Weg dorthin.
// Beide Komponenten bekommen deshalb die Zeilen-Optik als Variante, statt sie
// ein zweites Mal zu schreiben.

type MenuVariante = 'menue' | 'drawer';

function zeilenKlasse(variante: MenuVariante): string {
  return variante === 'drawer'
    ? 'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50'
    : 'flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50';
}

// ─── Rundgang im Benutzermenü ────────────────────────────────────────────────
//
// „Nochmal zeigen" setzt nur den Stand zurück; gestartet wird der Rundgang
// dann von der Komponente selbst, sobald der Server sagt, dass wieder etwas
// ansteht. Ein zweiter Weg, ihn direkt aufzurufen, wäre eine zweite Stelle,
// an der sich später etwas widersprechen kann.

function RundgangEintrag({
  onFertig,
  variante = 'menue',
}: {
  onFertig: () => void;
  variante?: MenuVariante;
}) {
  const queryClient = useQueryClient();
  const [laeuft, setLaeuft] = useState(false);

  const starten = async () => {
    setLaeuft(true);
    try {
      await rundgangNochmal();
      await queryClient.invalidateQueries({ queryKey: ['rundgang'] });
      onFertig();
    } catch {
      toast.error('Der Rundgang konnte nicht gestartet werden.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <button
      onClick={starten}
      disabled={laeuft}
      className={`${zeilenKlasse(variante)} disabled:opacity-50`}
    >
      <Compass className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      Rundgang starten
    </button>
  );
}

// ─── Vorschlag oder Fehler melden ────────────────────────────────────────────
//
// Steht in beiden Menüs. Die aktuelle Seite wird als Zustand mitgegeben: wer
// von hier aus einen Fehler meldet, kommt von der Stelle, an der er aufgetreten
// ist – und genau die will der Betreiber wissen.

function VorschlagEintrag({
  onFertig,
  variante = 'menue',
}: {
  onFertig: () => void;
  variante?: MenuVariante;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <button
      onClick={() => {
        onFertig();
        navigate('/vorschlag', { state: { von: location.pathname } });
      }}
      className={zeilenKlasse(variante)}
    >
      <MessageSquarePlus className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      Vorschlag oder Fehler
    </button>
  );
}

// ─── Chronik im Benutzermenü ─────────────────────────────────────────────────
//
// Das Stammbuch als PDF. Aufgeklappt statt sofort geladen, weil die Ordnung
// entscheidet, wozu das Buch taugt: nach Generationen eine Familiengeschichte,
// alphabetisch ein Nachschlagewerk, nach Geburtsjahr eine Zeitleiste. Ein Klick
// ohne Wahl hätte die anderen beiden Bücher unsichtbar gemacht.

function ChronikEintrag({
  onFertig,
  variante = 'menue',
}: {
  onFertig: () => void;
  variante?: MenuVariante;
}) {
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState<ChronikSortierung | null>(null);

  const holen = async (s: ChronikSortierung) => {
    setLaeuft(s);
    try {
      const { kapitel } = await ladeChronik(s);
      toast.success(`Chronik erstellt – ${kapitel} Kapitel.`);
      onFertig();
    } catch {
      toast.error('Die Chronik konnte nicht erstellt werden.');
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <>
      <button
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className={zeilenKlasse(variante)}
      >
        <BookOpen className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        <span className="flex-1">Chronik als PDF</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform ${offen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {offen && (
        <div className="border-y border-slate-100 bg-slate-50/60 py-1">
          {CHRONIK_SORTIERUNGEN.map((s) => (
            <button
              key={s}
              onClick={() => holen(s)}
              disabled={laeuft !== null}
              className="flex w-full items-start gap-2 px-4 py-2 pl-10 text-left hover:bg-slate-100 disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block text-sm text-slate-700">
                  {CHRONIK_SORTIERUNG_LABELS[s]}
                  {laeuft === s && (
                    <span className="ml-2 text-xs text-slate-400">wird gesetzt…</span>
                  )}
                </span>
                <span className="block text-[11px] leading-snug text-slate-400">
                  {CHRONIK_SORTIERUNG_BESCHREIBUNG[s]}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Stammbaum löschen Modal ─────────────────────────────────────────────────

function DeleteTreeModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const CONFIRM_WORD = 'LÖSCHEN';

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteEntireTree();
      queryClient.invalidateQueries();
      toast.success('Stammbaum wurde vollständig gelöscht.');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Löschen fehlgeschlagen.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Während des laufenden, unwiderruflichen Löschens darf das Modal nicht
  // geschlossen werden – sonst suggeriert es einen Abbruch, der nicht stattfand.
  const requestClose = () => { if (!loading) onClose(); };

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4" onClick={requestClose}>
      <div
        className="w-full max-w-md rounded-lg border border-red-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Stammbaum löschen</h2>
            <p className="text-sm text-slate-500">Diese Aktion kann nicht rückgängig gemacht werden!</p>
          </div>
        </div>

        <div className="mb-4 rounded-md bg-red-50 p-3">
          <p className="text-sm text-red-700">
            <strong>Alle Personen, Beziehungen, Medien und Quellen</strong> werden unwiderruflich gelöscht.
            Erstelle vorher ein Backup, wenn du die Daten behalten möchtest.
          </p>
        </div>

        <div className="mb-4">
          <label htmlFor="confirmDelete" className="mb-1 block text-sm font-medium text-slate-700">
            Tippe <span className="font-mono font-bold text-red-600">{CONFIRM_WORD}</span> zur Bestätigung
          </label>
          <input
            id="confirmDelete"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
            autoComplete="off"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
            placeholder={CONFIRM_WORD}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={requestClose}
            disabled={loading}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleDelete}
            disabled={confirmText !== CONFIRM_WORD || loading}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Wird gelöscht...' : 'Endgültig löschen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Drawer ────────────────────────────────────────────────────────────

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpenImport: () => void;
  onOpenDelete: () => void;
  onOpenPersonForm: () => void;
}

function MobileDrawer({ open, onClose, onOpenImport, onOpenDelete, onOpenPersonForm }: MobileDrawerProps) {
  const { text } = useTexte();
  const appName = text('app.name');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, clearAuth } = useAuthStore();
  const canManagePersons = useAuthStore((s) => s.hasPermission('persons.manage'));
  const darfExportieren = useAuthStore((s) => s.hasPermission('persons.view'));
  const istBesitzer = useIstBesitzer();
  const darfLoeschen = istBesitzer || isAdmin;
  // Der Import schreibt Personen – canManagePersons ist genau dieses Recht.
  const darfImportieren = canManagePersons;
  const darfDatenverwaltung = darfImportieren || darfLoeschen || darfExportieren;
  const navGroups = useFilteredNavGroups();
  const { hat } = useTreeFeatures();
  const [downloading, setDownloading] = useState<string | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Geschlossener Drawer ist nur aus dem Viewport geschoben – ohne `inert`
  // blieben alle Buttons darin per Tab fokussierbar und für Screenreader
  // lesbar (auch am Desktop). `inert` nimmt sie aus Fokus- und A11y-Baum.
  useEffect(() => {
    const el = drawerRef.current;
    if (el) (el as unknown as { inert: boolean }).inert = !open;
  }, [open]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Auch bei Fehler ausloggen
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  /** Wie `handleNav`, aber für Menüpunkte – die können auch nach außen führen. */
  const handleNavItem = (item: NavItem) => {
    springeZu(navigate, item);
    onClose();
  };

  const handleExport = async (item: (typeof EXPORT_ITEMS)[number]) => {
    setDownloading(item.path);
    try {
      await downloadExportFile(item.path, item.filename);
      toast.success(`${item.label} abgeschlossen.`);
      onClose();
    } catch {
      toast.error(`${item.label} fehlgeschlagen.`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      {/* Overlay (mobil + tablet; Desktop nutzt die Top-Navigation) */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[1250] xl:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer
          `h-app` (100dvh), nicht `h-full`: bei `position: fixed` bezieht sich
          `height: 100%` auf den Viewport OHNE eingeblendete Browserleisten. Am
          Telefon war der Drawer damit höher als der Bildschirm – der
          scrollbare Bereich rechnete mit Platz, den es nicht gab, und ließ
          sich deshalb nicht scrollen. Nach einem Neuladen mit eingeklappter
          Adressleiste stimmten die Zahlen wieder, was den Fehler sporadisch
          aussehen ließ. */}
      <div
        ref={drawerRef}
        className={`fixed left-0 top-0 h-app w-72 bg-white z-[1300] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col xl:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        aria-label="Navigation"
        aria-hidden={!open}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-2 text-lg font-semibold text-slate-800"
          >
            <TreePine className="h-6 w-6 text-emerald-600" aria-hidden="true" />
            <span>{appName}</span>
          </Link>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Menü schließen"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Aktiver Stammbaum */}
        <div className="border-b border-slate-200 px-4 py-3">
          <TreeSwitcher variant="drawer" onSwitched={onClose} />
        </div>

        {/* Scrollable nav content.
            `overscroll-contain` hält die Wischgeste im Menü: ohne sie reicht
            iOS sie am oberen und unteren Ende an die Seite dahinter weiter,
            und der Nutzer scrollt den Stammbaum statt der Liste. */}
        <div className="flex-1 overflow-y-auto overscroll-contain py-2">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-4 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {group.label}
                </p>
              </div>
              {group.items.map((item) => {
                const isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavItem(item)}
                    className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium ${
                      isActive
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {item.icon && (
                      <item.icon
                        className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-400'}`}
                        aria-hidden="true"
                      />
                    )}
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Person hinzufügen: für alle mit persons.manage (nicht nur Admins) */}
          {canManagePersons && (
            <div className="mb-2 border-t border-slate-100 pt-2">
              <button
                onClick={() => { onOpenPersonForm(); onClose(); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Person hinzufügen
              </button>
            </div>
          )}

          {/* Besitzer: der eigene Stammbaum und seine Mitglieder */}
          {istBesitzer && (
            <div className="mb-2 border-t border-slate-100 pt-2">
              <button
                onClick={() => { handleNav('/tree-settings'); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <TreePine className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Stammbaum
              </button>
              <button
                onClick={() => { handleNav('/members'); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Mitglieder
              </button>
            </div>
          )}

          {/* Chronik und Rundgang: am Telefon ist dieses Menü der einzige Weg
              dorthin – im Dropdown des Desktops stehen sie an derselben
              Stelle. */}
          <div className="mb-2 border-t border-slate-100 pt-2">
            {hat('chronik') && <ChronikEintrag onFertig={onClose} variante="drawer" />}
            <RundgangEintrag onFertig={onClose} variante="drawer" />
            <VorschlagEintrag onFertig={onClose} variante="drawer" />
          </div>

          {/* Datenverwaltung – dieselben Schranken wie im Dropdown des
              Desktops: Export für jeden, der die Daten sieht, Import für jeden,
              der Personen pflegen darf, Löschen für den Besitzer. */}
          {darfDatenverwaltung && (
            <div className="mb-2 border-t border-slate-100 pt-2">
              <div className="px-4 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Datenverwaltung
                </p>
              </div>
              {darfImportieren && (
                <button
                  onClick={() => { onOpenImport(); onClose(); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  Import
                </button>
              )}
              {darfExportieren &&
                EXPORT_ITEMS.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleExport(item)}
                    disabled={downloading !== null}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <span className="shrink-0 text-slate-400">
                      {downloading === item.path ? (
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                      ) : (
                        item.icon
                      )}
                    </span>
                    {item.label}
                  </button>
                ))}
              {/* Verwaltung und Stammbaum-Verwaltung sind in die
                  Betreiberkonsole gezogen – eigene Anwendung, eigene
                  Anmeldung. */}
              {darfLoeschen && (
                <button
                  onClick={() => { onOpenDelete(); onClose(); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Stammbaum löschen
                </button>
              )}
            </div>
          )}
        </div>

        {/* User info + logout footer */}
        <div className="border-t border-slate-200 p-4">
          <div className="mb-3">
            <p className="text-xs text-slate-400">Angemeldet als</p>
            <p className="truncate text-sm font-medium text-slate-900">{user?.email}</p>
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
              {isAdmin ? 'Admin' : 'Betrachter'}
            </span>
          </div>
          <button
            onClick={() => handleNav('/settings/security')}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 mb-2"
          >
            <Settings className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Einstellungen
          </button>
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-red-200 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Abmelden
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────

function Topbar() {
  const canManagePersons = useAuthStore((s) => s.hasPermission('persons.manage'));
  const canSearchPersons = useAuthStore((s) => s.hasPermission('search.view'));
  const navGroups = useFilteredNavGroups();
  const { direct, dropdownGroups } = splitNavForDesktop(navGroups);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Solange der Rundgang läuft, hält das Anliegen-Popup still. Zwei Overlays
  // übereinander sehen nicht nach zwei Hinweisen aus, sondern nach einem
  // Fehler – und wer neu ist, soll erst die Anwendung kennenlernen und dann
  // gefragt werden.
  const [rundgangLaeuft, setRundgangLaeuft] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { text } = useTexte();
  const appName = text('app.name');

  // Cmd+K (macOS) bzw. Ctrl+K → Schnellsuche
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  // Die Sprungziele der Schnellsuche kommen aus der Navigation – plus die
  // Personensuche, die dort nicht mehr steht. Sie kann etwas, das die
  // Schnellsuche nicht kann (Filter nach Geburtszeitraum); erreichbar bleiben
  // muss sie deshalb, nur eben nicht als zweites Suchfeld in der Kopfzeile.
  const darfSuchen = useAuthStore((s) => s.hasPermission('search.view'));
  const paletteItems = [
    ...navGroups.flatMap((group) => group.items),
    ...(darfSuchen
      ? [{ label: 'Personensuche (mit Zeitraum)', path: '/search', icon: Search }]
      : []),
  ];

  return (
    <>
      <header
        className="sticky top-0 z-[1200] flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm"
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}
      >
        {/* Left: Logo */}
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 text-lg font-semibold text-slate-800 hover:text-slate-900"
        >
          <TreePine className="h-6 w-6 text-emerald-600" aria-hidden="true" />
          <span>{appName}</span>
        </Link>

        {/* Aktiver Stammbaum (nur sichtbar, wenn es mehrere gibt) */}
        <div className="ml-3 hidden xl:block" data-rundgang="baumwechsel">
          <TreeSwitcher />
        </div>

        {/* Center: Desktop nav (Direktlinks + Dropdowns; unterhalb lg → Drawer) */}
        <nav className="hidden xl:flex items-center gap-0.5" aria-label="Hauptnavigation" data-rundgang="navigation">
          {direct.map((item) => (
            <NavDirectLink key={item.path} item={item} />
          ))}
          {dropdownGroups.map((group) => (
            <NavDropdown key={group.label} label={group.label} items={group.items} />
          ))}
        </nav>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Schnellsuche: Feld-Optik ab xl, darunter Icon-Button */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="hidden 2xl:flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-400 hover:border-slate-300 hover:text-slate-500"
            aria-label="Schnellsuche öffnen"
            data-rundgang="suche"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            <span className="pr-6">Suchen…</span>
            <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
              {isMac ? '⌘K' : 'Strg K'}
            </kbd>
          </button>
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex 2xl:hidden items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100"
            aria-label="Schnellsuche öffnen"
            data-rundgang="suche"
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Person-hinzufügen-Button: für alle Nutzer mit persons.manage */}
          {canManagePersons && (
            <div className="hidden xl:flex items-center gap-1.5" data-rundgang="person">
              <button
                onClick={() => setShowPersonModal(true)}
                className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span>Person</span>
              </button>
            </div>
          )}

          {/* User menu: desktop only */}
          <div className="hidden xl:block" data-rundgang="menue">
            <UserMenu
              onOpenImport={() => setShowImportModal(true)}
              onOpenDelete={() => setShowDeleteModal(true)}
            />
          </div>

          {/* Hamburger: mobile + tablet */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex xl:hidden items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100"
            aria-label="Menü öffnen"
            data-rundgang="menue"
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Globale Schnellsuche (Cmd/Ctrl+K) */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        navItems={paletteItems}
        canSearchPersons={canSearchPersons}
      />

      {/* Mobile drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenImport={() => setShowImportModal(true)}
        onOpenDelete={() => setShowDeleteModal(true)}
        onOpenPersonForm={() => setShowPersonModal(true)}
      />

      {/* Der Rundgang zeigt sich von selbst, wenn etwas ansteht – beim ersten
          Besuch und nach einem Upgrade; sonst rendert er nichts. Er steht
          hier und nicht im Seitenrumpf, weil er beim Start das Handy-Menü
          schließen muss: sonst läge es sichtbar, aber unbedienbar unter ihm.
          Für die Darstellung ist die Stelle ohne Belang, er hängt sich per
          Portal an den Seitenrumpf. */}
      <Rundgang
        onStart={() => {
          setDrawerOpen(false);
          setRundgangLaeuft(true);
        }}
        onEnde={() => setRundgangLaeuft(false)}
      />

      {/* Mitteilungen des Betreibers und laufende Abstimmungen. */}
      {!rundgangLaeuft && <AnliegenPopup />}

      {/* Modals */}
      {showPersonModal && (
        <PersonForm onClose={() => setShowPersonModal(false)} />
      )}
      {showImportModal && (
        <ImportModal onClose={() => setShowImportModal(false)} />
      )}
      {showDeleteModal && (
        <DeleteTreeModal onClose={() => setShowDeleteModal(false)} />
      )}
    </>
  );
}

// ─── AppLayout ────────────────────────────────────────────────────────────────

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { sidebarOpen } = useTreeStore();

  return (
    <div className="flex h-app flex-col bg-slate-50">
      <Topbar />

      <div className="flex flex-1 overflow-hidden">
        {/* Hauptbereich */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>

        {/* PersonPanel-Sidebar (nur wenn Person ausgewählt) */}
        {sidebarOpen && <PersonPanel />}
      </div>

    </div>
  );
}
